import type { Directive, DirectiveBinding } from 'vue';
import { onMouse } from './useOnMouse';
import { isIntersected } from './utils/isIntersecting';
import { hasDisabledElement } from './utils/hasDisabledElement';
import './fluent.css';

export interface FluentDirectiveBinding {
  effectSize?: number;
  size?: number;
  color?: string;
  enabled?: boolean;
  autoRadius?: boolean;
}

function fluent(element: HTMLElement, binding: DirectiveBinding<FluentDirectiveBinding>) {
  const defaults = {
    color: 'var(--color-primary-700)',
    size: 150,
    effectSize: 2,
    autoRadius: false,
  };

  const enabled = binding?.value?.enabled ?? true;
  const color = binding?.value?.color ?? defaults.color;
  const size = binding?.value?.size ?? defaults.size;
  const effectSize = binding?.value?.effectSize ?? defaults.effectSize;
  const autoRadius = binding?.value?.autoRadius ?? defaults.autoRadius;
  const computedStyle = getComputedStyle(element);
  element.style.setProperty('--fluent-size', `${effectSize}px`);

  if (autoRadius) {
    if (computedStyle.borderWidth !== '0px') {
      element.style.setProperty('--fluent-border-width', computedStyle.borderWidth);
    }
    if (computedStyle.outlineWidth !== '0px') {
      element.style.setProperty('--fluent-border-width', computedStyle.outlineWidth);
    }
  }

  element.style.setProperty('--fluent-border-radius', computedStyle.borderRadius);
  element.classList.add('daisy-fluent');

  (element as any).fluentListener = onMouse((event) => {
    drawEffect(event);
  });

  function onClick() {
    removeEffect();
  }

  element.addEventListener('click', onClick, {
    passive: true,
  });

  (element as any).removeClickListener = () => {
    element.removeEventListener('click', onClick);
  };

  let rect: any = undefined;
  let previousX = 0;
  let previousY = 0;

  function getCoordinates(event: MouseEvent) {
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    if (!rect || scrollX !== previousX || scrollY !== previousY) {
      previousX = scrollX;
      previousY = scrollY;
      rect = element.getBoundingClientRect();
    }

    const x = event.pageX - rect.left - window.scrollX;
    const y = event.pageY - rect.top - window.scrollY;

    return {
      x,
      y,
    };
  }

  function removeEffect() {
    element.style.removeProperty('--fluent-color');
  }

  function drawEffect(event: MouseEvent) {
    if (element === undefined || element === null) {
      return;
    }

    if (event === undefined) {
      return;
    }

    if (!enabled) {
      removeEffect();
      return;
    }

    if (hasDisabledElement(element)) {
      removeEffect();
      return;
    }

    const { x, y } = getCoordinates(event);

    if (!isIntersected(rect, event.clientX, event.clientY, size)) {
      removeEffect();
      return;
    }

    element.style.setProperty('--fluent-color', `radial-gradient(circle ${size}px at ${x}px ${y}px, ${color}, rgba(255,255,255,0))`);
  }
}

export const fluentDirective: Directive<HTMLInputElement, FluentDirectiveBinding> = {
  mounted(element, binding) {
    fluent(element, binding);
  },
  beforeUpdate(element) {
    (element as any)?.fluentListener?.();
    (element as any)?.removeClickListener?.();
  },
  updated(element, binding) {
    (element as any)?.fluentListener?.();
    (element as any)?.removeClickListener?.();
    fluent(element, binding);
  },
  beforeUnmount(element) {
    (element as any)?.fluentListener?.();
    (element as any)?.removeClickListener?.();
  },
  getSSRProps() {
    return undefined;
  },
};
